import { useState, useEffect } from "react";
import { useAuth, api } from "../App";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import PostCard from "../components/PostCard";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { 
  Edit, Phone, Calendar, Loader2, MapPin, Briefcase, 
  Users, User, Building
} from "lucide-react";

const ProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("posts");

  useEffect(() => {
    const fetchUserPosts = async () => {
      if (!user) return;
      try {
        const response = await api.get(`/posts/user/${user.id}`);
        setPosts(response.data);
      } catch (_error) {
        toast.error("Failed to load posts");
      } finally {
        setLoading(false);
      }
    };
    fetchUserPosts();
  }, [user]);

  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
  };

  if (!user) return null;

  const getInitials = () => {
    if (user.first_name) {
      return `${user.first_name[0]}${(user.last_name || "L")[0]}`.toUpperCase();
    }
    if (user.username) {
      return user.username.slice(0, 2).toUpperCase();
    }
    return user.phone.slice(-2);
  };

  const getFullName = () => {
    if (user.first_name) {
      return `${user.first_name} ${user.last_name || "LUNAWAT"}`;
    }
    return user.username || "No name set";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatJoinDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  const getIncomeLabel = (value) => {
    const labels = {
      'below-3l': 'Below ₹3 Lakhs',
      '3l-5l': '₹3 - 5 Lakhs',
      '5l-10l': '₹5 - 10 Lakhs',
      '10l-25l': '₹10 - 25 Lakhs',
      '25l-50l': '₹25 - 50 Lakhs',
      '50l-1cr': '₹50L - 1 Crore',
      'above-1cr': 'Above ₹1 Crore',
      'prefer-not': 'Not disclosed'
    };
    return labels[value] || value;
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-6 px-4" data-testid="profile-page">
        {/* Profile Header */}
        <Card className="p-6 mb-6" data-testid="profile-header">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <Avatar className="w-24 h-24 text-2xl">
              <AvatarImage src={user.avatar} alt={user.full_name || user.username || user.phone} />
              <AvatarFallback className="bg-primary/10 text-primary text-2xl font-bold">
                {getInitials()}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 text-center sm:text-left">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold" data-testid="profile-name">
                    {getFullName()}
                  </h1>
                  {/* Member Code - Show only for verified users */}
                  {user.verification_status === 'verified' && user.member_code && (
                    <div className="inline-flex items-center gap-2 mt-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-sm font-semibold" data-testid="member-code">
                      <span>Member ID:</span>
                      <span className="font-bold">{user.member_code}</span>
                    </div>
                  )}
                  {user.username && user.full_name && (
                    <p className="text-muted-foreground">@{user.username}</p>
                  )}
                  <div className="flex items-center justify-center sm:justify-start gap-2 text-muted-foreground mt-1">
                    <Phone className="w-4 h-4" />
                    <span data-testid="profile-phone">{user.phone}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  {!user.is_profile_complete && (
                    <Button 
                      onClick={() => navigate("/onboarding")}
                      variant="outline"
                      className="btn-hover"
                      data-testid="complete-profile-btn"
                    >
                      Complete Profile
                    </Button>
                  )}
                  <Button 
                    onClick={() => navigate("/profile/edit")}
                    className="btn-hover"
                    data-testid="edit-profile-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </div>

              {user.bio && (
                <p className="mt-4 text-foreground" data-testid="profile-bio">
                  {user.bio}
                </p>
              )}

              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 mt-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Joined {formatJoinDate(user.created_at)}</span>
                </div>
                {user.city && user.state && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{user.city}, {user.state}</span>
                  </div>
                )}
                {user.occupation && (
                  <div className="flex items-center gap-1">
                    <Briefcase className="w-4 h-4" />
                    <span className="capitalize">{user.occupation}</span>
                  </div>
                )}
                <div data-testid="post-count">
                  <span className="font-semibold text-foreground">{posts.length}</span> posts
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "posts" ? "default" : "outline"}
            onClick={() => setActiveTab("posts")}
            data-testid="tab-posts"
          >
            Posts
          </Button>
          <Button
            variant={activeTab === "about" ? "default" : "outline"}
            onClick={() => setActiveTab("about")}
            data-testid="tab-about"
          >
            About
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === "posts" ? (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Your Posts</h2>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-posts">
                <p className="text-lg mb-2">No posts yet</p>
                <p className="text-sm">Share your first post with the world!</p>
              </div>
            ) : (
              posts.map((post, index) => (
                <div 
                  key={post.id} 
                  className="animate-fade-in"
                  style={{ animationDelay: `${Math.min(index * 0.05, 0.3)}s` }}
                >
                  <PostCard 
                    post={post} 
                    onDelete={handlePostDeleted}
                    onUpdate={handlePostUpdated}
                  />
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-6" data-testid="about-section">
            {/* Personal Information */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Personal Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">First Name</p>
                  <p className="font-medium">{user.first_name || "Not set"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Name</p>
                  <p className="font-medium">{user.last_name || "LUNAWAT"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date of Birth</p>
                  <p className="font-medium">{formatDate(user.date_of_birth) || "Not set"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gender</p>
                  <p className="font-medium capitalize">{user.gender || "Not set"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{user.phone}</p>
                </div>
              </div>
            </Card>

            {/* Family Information */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Family Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Marital Status</p>
                  <p className="font-medium capitalize">{user.marital_status || "Not set"}</p>
                </div>
                {user.marital_status === 'married' && user.spouse_name && (
                  <div>
                    <p className="text-sm text-muted-foreground">Spouse Name</p>
                    <p className="font-medium">{user.spouse_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Children</p>
                  <p className="font-medium">{user.number_of_children || 0}</p>
                </div>
                {user.father_name && (
                  <div>
                    <p className="text-sm text-muted-foreground">Father&apos;s Name</p>
                    <p className="font-medium">{user.father_name}</p>
                  </div>
                )}
                {user.mother_name && (
                  <div>
                    <p className="text-sm text-muted-foreground">Mother&apos;s Name</p>
                    <p className="font-medium">{user.mother_name}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Address Information */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Address
              </h3>
              {user.address_line1 ? (
                <div className="space-y-1">
                  <p className="font-medium">{user.address_line1}</p>
                  {user.address_line2 && <p>{user.address_line2}</p>}
                  <p>{user.city}, {user.state} - {user.pincode}</p>
                  <p>{user.country}</p>
                </div>
              ) : (
                <p className="text-muted-foreground">Address not set</p>
              )}
            </Card>

            {/* Business Information */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                Business Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Occupation</p>
                  <p className="font-medium capitalize">{user.occupation || "Not set"}</p>
                </div>
                {user.company_name && (
                  <div>
                    <p className="text-sm text-muted-foreground">Company/Business</p>
                    <p className="font-medium">{user.company_name}</p>
                  </div>
                )}
                {user.designation && (
                  <div>
                    <p className="text-sm text-muted-foreground">Designation</p>
                    <p className="font-medium">{user.designation}</p>
                  </div>
                )}
                {user.annual_income && (
                  <div>
                    <p className="text-sm text-muted-foreground">Annual Income</p>
                    <p className="font-medium">{getIncomeLabel(user.annual_income)}</p>
                  </div>
                )}
                {user.work_address && (
                  <div className="sm:col-span-2">
                    <p className="text-sm text-muted-foreground">Work Address</p>
                    <p className="font-medium">{user.work_address}</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProfilePage;
